import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { BaseElement, IBaseElement } from '../models/base-element';
import { Confirm } from '../models/confirm';
import { ElementType } from '../models/element-type';
import { Environment } from '../models/environment.model';
import { File } from '../models/file';
import { Location } from '../models/location';
import { LocationRequest } from '../models/location-request';
import { Page } from '../models/page';
import { PageRequest } from '../models/page-request';
import { TranslationService } from '../services/translation.service';
import { DynamicState, ResetDynamicState } from '../stores/dynamic.store';
import { SetLocation } from '../stores/main.store';
import { AuthService } from './auth.service';
import { ElementService } from './element.service';
import { EnvironmentService } from './environment.service';
import { FileService } from './file.service';
import { HttpService } from './http.service';
import { ModalService } from './modal.service';
import { PortalErrorEvent } from '../models/portal-error-event';
import { PortalUpdateEvent } from '../models/portal-update-event';
import { ConnectionType } from '../models/connection-type.enum';
import { ConnectivityResult } from '../models/connectivity-result.model';
import { ErrorMessageExtractor } from './error-message-extractor.service';
import { StyleService } from './style.service';

@Injectable({
  providedIn: 'root',
})
export class RelayService {
  private readonly environment: Environment;

  constructor(
    private http: HttpService,
    private store: Store,
    private modalService: ModalService,
    private translation: TranslationService,
    private errorExtractor: ErrorMessageExtractor,
    private fileService: FileService,
    private authService: AuthService,
    private elementService: ElementService,
    private styleService: StyleService,
    environmentService: EnvironmentService
  ) {
    this.environment = environmentService.getEnvironment();
  }

  async getDefaultPage(): Promise<IBaseElement | undefined> {
    const pageRequest: PageRequest = {} as PageRequest;
    pageRequest.pageId = '00000000-4ad4-431b-b678-d25c7124fe37';
    return this.getPage(pageRequest);
  }

  async getDefaultMenu(): Promise<IBaseElement | undefined> {
    const pageRequest: PageRequest = {} as PageRequest;
    pageRequest.pageId = '11111111-4ad4-431b-b678-d25c7124fe37';
    return this.getPage(pageRequest);
  }

  async getDefaultSettings(): Promise<IBaseElement | undefined> {
    const pageRequest: PageRequest = {} as PageRequest;
    pageRequest.pageId = '22222222-4ad4-431b-b678-d25c7124fe37';
    return this.getPage(pageRequest);
  }

  async getDefaultFooter(): Promise<IBaseElement | undefined> {
    const pageRequest: PageRequest = {} as PageRequest;
    pageRequest.pageId = '33333333-4ad4-431b-b678-d25c7124fe37';
    return this.getPage(pageRequest);
  }

  async loadDefaultPage(): Promise<IBaseElement | undefined> {
    const cachedPage = this.store.selectSnapshot(DynamicState.page);
    if (cachedPage) {
      const page = cachedPage as Page;
      if (page.themeOverride) {
        // Defer so tenant colors (set synchronously via NGXS) are available when merging.
        setTimeout(() => this.styleService.applyPageOverride(page.themeOverride!), 0);
      }
      return cachedPage;
    }
    return await this.getDefaultPage();
  }

  async loadDefaultMenu(): Promise<IBaseElement | undefined> {
    if (this.store.selectSnapshot(DynamicState.menu)) {
      return this.store.selectSnapshot(DynamicState.menu) ?? undefined;
    }
    return await this.getDefaultMenu();
  }

  async loadDefaultFooter(): Promise<IBaseElement | undefined> {
    if (this.store.selectSnapshot(DynamicState.footer)) {
      return this.store.selectSnapshot(DynamicState.footer) ?? undefined;
    }
    return await this.getDefaultFooter();
  }

  async resetState() {
    this.store.dispatch(new ResetDynamicState());
  }

  async loadDefaultPages(resetState?: boolean) {
    if (resetState) {
      await this.resetState();
    }
    this.loadDefaultMenu();
    this.loadDefaultPage();
    this.loadDefaultFooter();
    this.getDefaultSettings();
  }

  async setLocation(location: Location): Promise<void> {
    const locationRequest: LocationRequest = {} as LocationRequest;
    locationRequest.locationGuid = location.locationGuid;
    locationRequest.relationId = location.relationId;

    await this.http
      .post<Location[], LocationRequest>(`${this.environment.apiUrl}/relay/Location/set`, locationRequest)
      .then(() => {
        this.store.dispatch(new SetLocation(location));
      });
    this.loadDefaultPages(true);
    await this.getDefaultSettings();
  }

  async getPage(
    pageRequest: PageRequest,
    saveToHistory?: boolean,
    target?: EventTarget
  ): Promise<IBaseElement | undefined> {
    if (!target) {
      target = window;
    }
    return this.http
      .post<BaseElement, PageRequest>(`${this.environment.apiUrl}/relay/page`, pageRequest)
      .then(async (element: BaseElement): Promise<IBaseElement | undefined> => {
        if (!element) {
          this.dispatchErrorEvent(pageRequest, this.translation.getTranslation('EMPTY_RESPONSE'), target);
        } else {
          return await this.whatToDo(element, pageRequest, target, saveToHistory ?? true);
        }
        return;
      })
      .catch((error: any) => {
        this.handleError(pageRequest, error, target);
        return undefined;
      });
  }

  handleError(pageRequest: PageRequest, error: any, target?: EventTarget) {
    const errorMessage = this.errorExtractor.extractErrorMessage(error);
    const translatedMessage = this.errorExtractor.translateErrorCode(errorMessage);
    this.dispatchErrorEvent(pageRequest, translatedMessage, target);
  }

  dispatchErrorEvent(pageRequest: PageRequest, message: string, target?: EventTarget) {
    (target ?? window).dispatchEvent(
      new CustomEvent(PortalErrorEvent.EVENT_NAME, {
        bubbles: true,
        detail: { pageRequest: pageRequest, errorMessage: message } as PortalErrorEvent,
      })
    );
  }

  async whatToDo(element: BaseElement, pageRequest: PageRequest, target: EventTarget, saveToHistory: boolean): Promise<IBaseElement> {
    switch (element.elementType) {
      case ElementType.file:
        await this.elementService.initializeElement(element);
        this.fileService.handle(element as File);
        break;
      case ElementType.logout:
        await this.elementService.initializeElement(element);
        this.authService.logout();
        this.modalService.confirm(element as Confirm);
        break;
      default:
        break;
    }

    if (element.elementType === ElementType.page) {
      const page = element as Page;
      if (page.themeOverride) {
        this.styleService.applyPageOverride(page.themeOverride);
      } else {
        this.styleService.revertToTenantTheme();
      }
    }

    target.dispatchEvent(
      new CustomEvent(PortalUpdateEvent.EVENT_NAME, {
        bubbles: true,
        detail: {
          element: element,
          request: pageRequest,
          saveToHistory: saveToHistory,
        } as PortalUpdateEvent,
      })
    );

    return element;
  }

  async connectivityTest(tenantId: string, connectionType: ConnectionType): Promise<ConnectivityResult> {
    const result = await this.http
      .post<ConnectivityResult, object>(`${this.environment.apiUrl}/relay/connection/test`, { tenantId, connectionType });
    return result;
  }
}
